import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider 
} from '@mui/material';
import ArrowRight from '@mui/icons-material/ArrowRight';
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import HomeIcon from '@mui/icons-material/Home';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';

/**
 * A component that displays static infographics for fall prevention modules
 * @param {Object} props - Component props
 * @param {String} props.module - The module name (e.g., "UnderstandingFallRisks")
 * @returns {JSX.Element} - The rendered component
 */
const StaticInfographic = ({ module }) => {
  // Create icon components map
  const iconComponents = {
    InfoIcon: <InfoIcon />,
    PersonIcon: <PersonIcon />,
    WarningIcon: <WarningIcon />,
    MedicalServicesIcon: <MedicalServicesIcon />,
    PsychologyIcon: <PsychologyIcon />,
    AccessibilityIcon: <AccessibilityIcon />,
    HomeIcon: <HomeIcon />,
    TransferWithinAStationIcon: <TransferWithinAStationIcon />
  };

  // Map of infographics by module
  const infographicMap = {
    UnderstandingFallRisks: {
      title: "Fall Risk Factors",
      sections: [
        {
          title: "Intrinsic Factors",
          description:
            "Physical and health-related factors that increase fall risk",
          color: "#e57373", // Red
          icon: iconComponents.PersonIcon,
          items: [
            "Age-related changes in balance and gait",
            "Muscle weakness, particularly in lower extremities",
            "Visual impairments and poor depth perception",
            "Cognitive impairment and confusion",
            "For Parkinson's patients: Monitor for freezing of gait and postural instability",
          ],
        },
        {
          title: "Environmental Hazards",
          description: "Home and surroundings factors that contribute to falls",
          color: "#64b5f6", // Blue
          icon: iconComponents.HomeIcon,
          items: [
            "Poor lighting and high-glare surfaces",
            "Loose rugs and cluttered walkways",
            "Lack of grab bars in bathrooms",
            "Uneven surfaces and thresholds",
            "For mobility limitations: Ensure wider pathways for walkers/wheelchairs",
          ],
        },
        {
          title: "Medication Risks",
          description: "Medication effects that may increase fall risk",
          color: "#4db6ac", // Teal
          icon: iconComponents.MedicalServicesIcon,
          items: [
            "Sedatives and sleep medications",
            "Blood pressure medications causing orthostatic hypotension",
            "Multiple medications (polypharmacy)",
            "For diabetes: Monitor for hypoglycemia which can cause dizziness",
            "For arthritis: Pain medications may cause drowsiness",
          ],
        },
        {
          title: "Risk Assessment Tools",
          description: "Methods to evaluate and quantify fall risk",
          color: "#9575cd", // Purple
          icon: iconComponents.PsychologyIcon,
          items: [
            "Timed Up and Go (TUG) test",
            "Berg Balance Scale",
            "Morse Fall Scale for healthcare settings",
            "For dementia: Use specialized cognitive assessments",
            "Regular reassessment as conditions change",
          ],
        },
      ],
    },
    RiskAssessment: {
      title: "Fall Risk Assessment Process",
      sections: [
        {
          title: "Initial Assessment",
          description: "First steps in evaluating fall risk",
          color: "#64b5f6", // Blue
          icon: iconComponents.InfoIcon,
          items: [
            "Review medical history and previous falls",
            "Medication review for high-risk medications",
            "For Parkinson's: Assess freezing episodes and tremor severity",
            "For arthritis: Evaluate joint pain and mobility limitations",
            "For dementia: Assess cognitive status and awareness",
          ],
        },
        {
          title: "Physical Evaluation",
          description:
            "Assessment of physical capabilities related to fall risk",
          color: "#4db6ac", // Teal
          icon: iconComponents.AccessibilityIcon,
          items: [
            "Balance and gait assessment",
            "Muscle strength testing",
            "Vision and hearing screening",
            "For diabetes: Check for peripheral neuropathy",
            "For mobility limitations: Evaluate assistive device usage",
          ],
        },
        {
          title: "Environmental Assessment",
          description: "Evaluation of living environment for fall hazards",
          color: "#ffb74d", // Orange
          icon: iconComponents.HomeIcon,
          items: [
            "Home safety checklist",
            "Lighting assessment",
            "Bathroom safety evaluation",
            "Furniture arrangement and accessibility",
            "For wheelchair users: Check doorway widths and turning spaces",
          ],
        },
        {
          title: "Risk Stratification",
          description: "Categorizing fall risk level to guide interventions",
          color: "#9575cd", // Purple
          icon: iconComponents.WarningIcon,
          items: [
            "Low, moderate, and high-risk classifications",
            "Individualized risk factors identification",
            "For dementia: Consider wandering risk and supervision needs",
            "For Parkinson's: Assess medication timing and 'on/off' periods",
            "Documentation and communication of risk level",
          ],
        },
      ],
    },
    CaregiverBestPractices: {
      title: "Safe Transfer and Mobility Techniques",
      sections: [
        {
          title: "Preparation for Transfers",
          description: "Steps to take before assisting with movement",
          color: "#4db6ac", // Teal
          icon: iconComponents.InfoIcon,
          items: [
            "Clear the path of obstacles",
            "Position assistive devices appropriately",
            "For arthritis: Allow extra time for joint stiffness",
            "For Parkinson's: Time transfers during medication 'on' periods",
            "Explain the process clearly before beginning",
          ],
        },
        {
          title: "Proper Body Mechanics",
          description:
            "Techniques to prevent caregiver injury during transfers",
          color: "#9575cd", // Purple
          icon: iconComponents.PersonIcon,
          items: [
            "Maintain wide base of support",
            "Bend knees, not waist",
            "Keep care recipient close to body during transfer",
            "For heavier individuals: Use mechanical lift when appropriate",
            "For mobility limitations: Assess need for additional assistance",
          ],
        },
        {
          title: "Transfer Techniques",
          description: "Methods for safely moving between positions",
          color: "#64b5f6", // Blue
          icon: iconComponents.TransferWithinAStationIcon,
          items: [
            "Bed to wheelchair transfers",
            "Sit-to-stand assistance",
            "Toilet transfers with appropriate supports",
            "For dementia: Use simple, consistent verbal cues",
            "For diabetes: Check for foot injuries before and after transfers",
          ],
        },
        {
          title: "Walking Assistance",
          description: "Safe methods for supporting ambulation",
          color: "#ffb74d", // Orange
          icon: iconComponents.AccessibilityIcon,
          items: [
            "Proper gait belt usage",
            "Positioning for optimal support",
            "Managing assistive devices during walks",
            "For Parkinson's: Be prepared for freezing episodes",
            "For arthritis: Accommodate slower pace and joint pain",
          ],
        },
      ],
    },
    EmergencyResponse: {
      title: "Fall Emergency Response Protocol",
      sections: [
        {
          title: "Assessment Phase",
          description: "Initial evaluation of the fallen individual",
          color: "#ba68c8", // Purple
          icon: iconComponents.InfoIcon,
          items: [
            "Check for responsiveness and consciousness",
            "Assess breathing and circulation",
            "For diabetes: Check for signs of hypoglycemia",
            "For Parkinson's: Note if fall occurred during 'off' period",
            "Do not move if head, neck or spine injury is suspected",
          ],
        },
        {
          title: "Alert and Communication",
          description: "Proper notification procedures",
          color: "#e57373", // Red
          icon: iconComponents.WarningIcon,
          items: [
            "Call emergency services (911) for serious injuries",
            "Alert other caregivers or family members",
            "For dementia: Provide calming reassurance during the process",
            "For mobility limitations: Inform responders of specific needs",
            "Provide clear information about the fall and condition",
          ],
        },
        {
          title: "Immediate Care",
          description: "Support provided while waiting for help",
          color: "#4caf50", // Green
          icon: iconComponents.MedicalServicesIcon,
          items: [
            "Keep the person warm and comfortable",
            "Apply direct pressure to control any bleeding",
            "For arthritis: Be mindful of joint pain when positioning",
            "For diabetes: Monitor for changes in consciousness",
            "Provide reassurance and emotional support",
          ],
        },
      ],
    },
    TechnologyInnovations: {
      title: "Fall Prevention Technology Solutions",
      sections: [
        {
          title: "Wearable Devices",
          description:
            "Technology worn on the body to detect and prevent falls",
          color: "#ff8a65", // Orange
          icon: iconComponents.InfoIcon,
          items: [
            "Fall detection watches with GPS tracking",
            "Smart insoles for gait analysis",
            "For Parkinson's: Devices that detect freezing of gait",
            "For dementia: GPS trackers with geofencing alerts",
            "For diabetes: Combined fall and glucose monitoring systems",
          ],
        },
        {
          title: "Smart Home Solutions",
          description: "Environmental technologies that enhance safety",
          color: "#4db6ac", // Teal
          icon: iconComponents.HomeIcon,
          items: [
            "Motion-activated lighting systems",
            "Voice-controlled home automation",
            "Pressure sensors for bed and chair exits",
            "For mobility limitations: Smart doorways and automatic doors",
            "For arthritis: Touch-free faucets and fixtures",
          ],
        },
        {
          title: "Monitoring Systems",
          description: "Technologies that provide oversight and alerts",
          color: "#64b5f6", // Blue
          icon: iconComponents.WarningIcon,
          items: [
            "Camera systems with AI fall detection",
            "Non-wearable radar-based fall detection",
            "For dementia: Activity pattern monitoring with alerts for changes",
            "For Parkinson's: Movement analysis systems",
            "Remote monitoring platforms for caregivers",
          ],
        },
        {
          title: "Telehealth Applications",
          description: "Remote healthcare services for fall prevention",
          color: "#9575cd", // Purple
          icon: iconComponents.MedicalServicesIcon,
          items: [
            "Virtual physical therapy sessions",
            "Remote balance assessments",
            "For diabetes: Combined fall risk and glucose management",
            "For arthritis: Pain management and mobility consultations",
            "Medication management and adherence monitoring",
          ],
        },
      ],
    },
    InitialAssessment: {
      title: "Initial Assessment and Recognition",
      sections: [
        {
          title: "Mild Airway Obstruction",
          description:
            "Identifying mild obstruction helps ensure patients are managed correctly without unnecessary intervention.",
          color: "#ba68c8", // Purple
          icon: iconComponents.InfoIcon,
          items: [
            "Signs and Symptoms: Patients can cough forcefully, speak, and breathe with some difficulty. They may show signs of distress but can still draw air in.",
            "Recommended Action: Encourage the patient to continue coughing forcefully. This natural reflex is the most effective way to expel the obstruction.",
            "Monitoring: While assisting the patient, closely observe for signs of worsening obstruction, such as weakening cough or increased difficulty breathing.",
          ],
        },
        {
          title: "Severe Airway Obstruction",
          description:
            "Severe obstruction requires immediate intervention to prevent respiratory failure.",
          color: "#4db6ac", // Teal
          icon: iconComponents.HomeIcon,
          items: [
            "Key Signs: The patient may be unable to speak, breathe, or cough effectively. Common signs include clutching the throat (universal choking sign), wide eyes, and panic.",
            "Cyanosis and Hypoxia: Patients may develop bluish skin (especially around lips and face) due to low oxygen levels. This is a critical warning sign of oxygen deprivation.",
            "Immediate Action: For conscious patients, apply the Heimlich maneuver (abdominal thrusts) until the object is expelled or the patient becomes unconscious. For unconscious patients, begin CPR immediately.",
            "Special Considerations: Pregnant or obese patients should receive chest thrusts instead of abdominal thrusts to avoid injury.",
          ],
        },
      ],
    },
  };

  // Get the appropriate infographic based on the module name
  const infographic = infographicMap[module] || {
    title: "Fall Prevention Information",
    sections: [
      {
        title: "General Considerations",
        description: "Key points to remember for fall prevention",
        color: "#64b5f6",
        icon: iconComponents.InfoIcon,
        items: [
          "Assess individual risk factors regularly",
          "Create a safe environment by removing hazards",
          "For Parkinson's disease: Be aware of freezing episodes",
          "For arthritis: Allow extra time for movements",
          "For dementia: Provide clear, simple instructions"
        ]
      }
    ]
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'rgba(100, 181, 246, 0.05)' }}>
      <Typography variant="h5" gutterBottom sx={{ color: '#64b5f6', fontWeight: 600 }}>
        {infographic.title}
      </Typography>
      
      <Box sx={{ mt: 3 }}>
        {infographic.sections.map((section, index) => (
          <Box key={index} sx={{ mb: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 1, 
              p: 1, 
              bgcolor: section.color || '#64b5f6', 
              color: 'white',
              borderRadius: 1
            }}>
              <Box sx={{ mr: 1 }}>
                {section.icon}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                {section.title}
              </Typography>
            </Box>
            
            <Typography variant="body1" sx={{ color: '#555', mb: 1, pl: 2 }}>
              {section.description}
            </Typography>
            
            <List dense sx={{ bgcolor: 'white', borderRadius: 1, mb: 2 }}>
              {section.items && section.items.map((item, itemIndex) => (
                <React.Fragment key={itemIndex}>
                  <ListItem>
                    <ListItemIcon>
                      <ArrowRight sx={{ color: section.color || '#64b5f6' }} />
                    </ListItemIcon>
                    {typeof item === 'string' ? (
                      <ListItemText primary={item} />
                    ) : (
                      <ListItemText primary={item.primary} secondary={item.secondary} />
                    )}
                  </ListItem>
                  {itemIndex < section.items.length - 1 && (
                    <Divider variant="inset" component="li" />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default StaticInfographic;
